import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@tinct/web-components';
import { CreatedServiceAccount, CreateServiceAccount } from '@tinct/core-api-models';
import { useCreateServiceAccountForm } from '../hooks/use-create-service-account-form';
import { X } from 'lucide-react';
import { createConnectedInputHelpers } from '@tinct/web-form';
import { Controller, FormProvider } from 'react-hook-form';
import { useState } from 'react';
import { ServiceAccountSecretReveal } from '../components/service-account-secret-reveal';
import { ServiceAccountPermissionPicker } from '../components/service-account-permission-picker';

type CreateServiceAccountModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
};

export const CreateServiceAccountModal = ({
  open,
  onOpenChange,
  organizationId,
}: CreateServiceAccountModalProps) => {
  const [createdServiceAccount, setCreatedServiceAccount] =
    useState<CreatedServiceAccount | null>(null);

  const { form, handleSubmit, isLoading, isCatalogReady, isSuccess, reset } =
    useCreateServiceAccountForm({
      organizationId,
      onSuccess: (created) => setCreatedServiceAccount(created),
    });

  const { ControlledTextInput } = createConnectedInputHelpers<CreateServiceAccount>();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      setCreatedServiceAccount(null);
    }
    onOpenChange(open);
  };

  const isSuccessState = isSuccess && createdServiceAccount;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-6 rounded-3xl p-8 shadow-lg sm:max-w-lg"
      >
        {isSuccessState ? (
          <>
            <div className="flex items-start gap-4">
              <DialogTitle className="flex-1 font-heading text-xl font-semibold tracking-tight text-foreground">
                Service account created
              </DialogTitle>
              <DialogClose className="shrink-0 opacity-70 transition-opacity hover:opacity-100">
                <X className="size-6" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <ServiceAccountSecretReveal
              clientId={createdServiceAccount.clientId ?? ''}
              clientSecret={createdServiceAccount.clientSecret ?? ''}
            />

            <Button size="lg" className="w-full" onClick={() => handleOpenChange(false)}>
              Done
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <DialogTitle className="flex-1 font-heading text-xl font-semibold tracking-tight text-foreground">
                Create a service account
              </DialogTitle>
              <DialogClose className="shrink-0 opacity-70 transition-opacity hover:opacity-100">
                <X className="size-6" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>

            <FormProvider {...form}>
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <ControlledTextInput
                    name="name"
                    label="Name *"
                    placeholder="e.g. CI robot"
                    direction="vertical"
                  />
                  <p className="text-sm text-muted-foreground">
                    A label to identify the integration using this credential.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">Permissions *</span>
                  <Controller
                    control={form.control}
                    name="permissions"
                    render={({ field }) => (
                      <ServiceAccountPermissionPicker
                        organizationId={organizationId}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  {form.formState.errors.permissions && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.permissions.message}
                    </p>
                  )}
                </div>

                {form.formState.errors.root && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.root.message}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => handleOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={!isCatalogReady}
                  >
                    Create
                  </Button>
                </div>
              </form>
            </FormProvider>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
